import useSound from 'use-sound';
import { useCallback, useMemo } from 'react';
import type { InteractionSettings } from '../types';
import { featureSoundUrls } from '@services/interactions-service';
import { featureSoundConfig } from '../main/Settings/units/SoundSettings';

interface UseInteractionSoundProps {
  featureSettings: {
    setting?: Partial<InteractionSettings>;
    settings?: Partial<InteractionSettings>;
    [key: string]: any;
  };
  soundKey: string;
  isEnabledKey: string;
  options?: {
    playbackRate?: number;
    volume?: number;
    duration?: number;
    loop?: boolean;
  };
  isMuted?: boolean;
}

const useInteractionSound = ({ featureSettings, soundKey, isEnabledKey, options, isMuted = true }: UseInteractionSoundProps) => {
  const interactionSettings = featureSettings.setting || featureSettings.settings;
  const featureSoundConfigss = featureSoundConfig[featureSettings.type as keyof typeof featureSoundConfig];
  const defaultSound = featureSoundConfigss?.soundOptions.find(option => option.isDefault);
  const customSoundKey = interactionSettings?.[soundKey as keyof InteractionSettings];
  const currentSelectedSound = customSoundKey || defaultSound?.value;

  const { soundURL, isSoundEnabled } = useMemo(() => {
    const soundOptions = featureSoundUrls[featureSettings.type as keyof typeof featureSoundUrls] || {};
    const url = soundOptions[currentSelectedSound as keyof typeof soundOptions] as string || defaultSound?.url;
    const enabled = interactionSettings?.[isEnabledKey as keyof InteractionSettings] as boolean;
    return {
      soundURL: url,
      isSoundEnabled: enabled
    };
  }, [featureSettings.type, interactionSettings, currentSelectedSound, isEnabledKey]);

  if (!soundURL) {
    console.error(`Sound URL for ${interactionSettings?.[soundKey as keyof InteractionSettings]} not found`);
    return { playSound: () => { } };
  }

  const volume = isMuted ? 0 : (options?.volume || 1.0);

  const [play, { stop, sound }] = useSound(soundURL, {
    playbackRate: options?.playbackRate || 0.75,
    volume,
    loop: options?.loop ?? false
  });

  const playSound = useCallback(() => {
    if (isSoundEnabled) {
      play();
      if (options?.duration) {
        setTimeout(() => {
          stop();
        }, options.duration);
      }
    }
  }, [isSoundEnabled, isMuted, play]);

  return { playSound, stopSound: stop, sound };
};

export default useInteractionSound;
